import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { FileText, Download, CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const useCountdown = (fco) => {
  const [remaining, setRemaining] = useState(fco?.seconds_remaining || 0);
  useEffect(() => {
    setRemaining(fco?.seconds_remaining || 0);
    if (!fco || fco.status !== 'issued') return;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [fco]);
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  return { remaining, label: `${d}d ${h}h ${m}m ${s}s` };
};

const STATUS_STYLES = {
  issued:       { label: 'Awaiting acknowledgement', className: 'text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10', Icon: Clock },
  acknowledged: { label: 'Acknowledged',             className: 'text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10', Icon: CheckCircle2 },
  rejected:     { label: 'Rejected',                 className: 'text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10', Icon: XCircle },
  superseded:   { label: 'Superseded',               className: 'text-[#94A3B8] border-[#1E293B] bg-white/5',         Icon: RefreshCw },
};

/**
 * FCO card + buyer actions.
 *
 * Props:
 *  - dealId
 *  - fco             latest FCO (or null)
 *  - onChanged(fco)  refresh callback
 *  - viewerRole      "buyer" | "seller" | "admin"
 *  - canReissue      seller reissue permitted (deal in_progress)
 *  - onReissue       parent opens the seller wizard
 */
export default function FCOViewer({ dealId, fco, onChanged, viewerRole, canReissue, onReissue }) {
  const { getAuthHeader } = useAuth();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { label: countdown, remaining } = useCountdown(fco);

  if (!fco) {
    return (
      <Card className="bg-[#121A2F] border-[#1E293B] p-6 text-center text-[#94A3B8]" data-testid="fco-empty">
        <FileText className="w-10 h-10 mx-auto mb-3 text-[#64748B]" />
        <p className="text-sm">
          {viewerRole === 'seller'
            ? 'No FCO issued on this deal yet. Click "Generate FCO" to draft one.'
            : 'The seller has not issued a Full Corporate Offer yet.'}
        </p>
      </Card>
    );
  }

  const s = STATUS_STYLES[fco.status] || STATUS_STYLES.issued;
  const Icon = s.Icon;

  const acknowledge = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/deals/${dealId}/fco/acknowledge`, {},
        { headers: getAuthHeader() });
      toast.success('FCO acknowledged — PDF is now available for download');
      onChanged?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge FCO');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) {
      toast.error('Please provide a rejection reason'); return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/deals/${dealId}/fco/reject`,
        { reason: rejectReason.trim() },
        { headers: getAuthHeader() });
      toast.success('FCO rejected — the seller can issue a revised version');
      setRejectOpen(false);
      setRejectReason('');
      onChanged?.(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to reject FCO');
    } finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API_URL}/deals/${dealId}/fco.pdf`,
        { headers: getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fco.fco_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // axios with responseType:'blob' wraps error bodies in a Blob too —
      // decode it so the real backend message surfaces to the seller.
      let msg = err.message || 'Download failed';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          const detail = parsed?.detail;
          if (typeof detail === 'string') msg = detail;
          else if (detail?.message) msg = detail.message;
        } catch { /* keep default msg */ }
      } else {
        const detail = err.response?.data?.detail;
        msg = typeof detail === 'string' ? detail : detail?.message || msg;
      }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const canBuyerAct = viewerRole === 'buyer' && fco.status === 'issued' && remaining > 0;
  const canDownload =
    (viewerRole === 'seller' || viewerRole === 'admin' || fco.status === 'acknowledged');

  return (
    <Card className="bg-[#121A2F] border-[#1E293B] p-6" data-testid="fco-card">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#94A3B8]">Full Corporate Offer</p>
          <p className="text-white font-semibold text-lg mt-0.5" data-testid="fco-number">{fco.fco_number}</p>
          <p className="text-xs text-[#64748B] mt-1">Issued {fmt(fco.issued_at)} · Valid until {fmt(fco.valid_until)}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${s.className}`}
              data-testid="fco-status-badge">
          <Icon className="w-3.5 h-3.5" />{s.label}
        </span>
      </div>

      {fco.status === 'issued' && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-2 text-center">
          {countdown.split(' ').map((seg, i) => (
            <div key={i} className="rounded-lg border border-[#1E293B] bg-[#0B1120] px-2 py-2"
                 data-testid={`fco-countdown-cell-${i}`}>
              <span className="text-lg font-semibold text-[#F59E0B]">{seg.slice(0, -1)}</span>
              <span className="text-[10px] uppercase text-[#64748B] ml-1">{seg.slice(-1)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Stat k="Commodity"  v={fco.commodity?.name || '—'} />
        <Stat k="Origin"     v={fco.commodity?.origin || '—'} />
        <Stat k="Quantity"   v={`${fco.commercial?.quantity || '—'} ${fco.commercial?.unit || ''}`} />
        <Stat k="Total"
              v={`${fco.commercial?.currency || 'USD'} ${(fco.commercial?.total_value || 0).toLocaleString()}`}
              highlight />
      </div>

      {(fco.specs || []).length > 0 && (
        <div className="mt-4 rounded-lg border border-[#1E293B] overflow-hidden" data-testid="fco-specs-table">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.03] text-[#94A3B8] uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Parameter</th>
                <th className="text-left px-3 py-2">Typical</th>
                <th className="text-left px-3 py-2">Rejection</th>
              </tr>
            </thead>
            <tbody>
              {fco.specs.map((sp) => (
                <tr key={sp.key} className="border-t border-[#1E293B]">
                  <td className="px-3 py-2 text-white">{sp.label}</td>
                  <td className="px-3 py-2 text-[#F59E0B] font-medium">{sp.value}{sp.unit ? ` ${sp.unit}` : ''}</td>
                  <td className="px-3 py-2 text-[#94A3B8]">{sp.rejection || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {fco.rejection?.reason && (
        <div className="mt-3 rounded-md border border-[#EF4444]/30 bg-[#EF4444]/10 px-3 py-2 text-xs text-[#FCA5A5]"
             data-testid="fco-rejection-reason">
          Rejected: {fco.rejection.reason}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {canBuyerAct && (
          <>
            <Button onClick={acknowledge} disabled={busy}
                    data-testid="fco-acknowledge-btn"
                    className="bg-[#10B981] hover:bg-[#059669] text-white font-semibold">
              <CheckCircle2 className="w-4 h-4 mr-1.5" />Acknowledge FCO
            </Button>
            <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={busy}
                    data-testid="fco-reject-btn"
                    className="bg-transparent border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">
              <XCircle className="w-4 h-4 mr-1.5" />Reject
            </Button>
          </>
        )}
        {canDownload && (
          <Button variant="outline" onClick={downloadPDF} disabled={downloading}
                  data-testid="fco-download-btn"
                  className="bg-transparent border-[#1E293B] text-white hover:bg-white/5">
            <Download className="w-4 h-4 mr-1.5" />{downloading ? 'Preparing…' : 'Download PDF'}
          </Button>
        )}
        {viewerRole === 'seller' && canReissue && fco.status !== 'acknowledged' && (
          <Button variant="outline" onClick={onReissue}
                  data-testid="fco-reissue-btn"
                  className="bg-transparent border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10">
            <RefreshCw className="w-4 h-4 mr-1.5" />
            {fco.status === 'issued' ? 'Supersede with New FCO' : 'Issue Revised FCO'}
          </Button>
        )}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-[#121A2F] border-[#1E293B] text-white max-w-md" data-testid="fco-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reject this FCO</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Explain what you'd like the seller to change. Do not include phone numbers, emails, or URLs.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. Please reissue at USD 1200 / ton or extend loading to 60 days."
                    data-testid="fco-reject-reason-input"
                    className="bg-[#0B1120] border-[#1E293B] text-white" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRejectOpen(false)}
                    className="bg-transparent border-[#1E293B] text-white">Cancel</Button>
            <Button onClick={reject} disabled={busy}
                    data-testid="fco-reject-confirm-btn"
                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold">
              Reject FCO
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

const Stat = ({ k, v, highlight }) => (
  <div className="rounded-md border border-[#1E293B] bg-white/[0.02] px-3 py-2">
    <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{k}</p>
    <p className={`text-sm mt-0.5 ${highlight ? 'text-[#F59E0B] font-semibold' : 'text-white'}`}>{v}</p>
  </div>
);
